import { FileText, ExternalLink, Download, AlertTriangle } from 'lucide-react';

interface Resume {
  id: string;
  original_filename: string;
  original_file_url: string;
  job_url: string | null;
  job_description: string | null;
  optimized_file_url: string | null;
  status: 'pending' | 'processing' | 'completed' | 'error';
  error_message?: string | null;
  created_at: string;
}

interface ResumeListProps {
  resumes: Resume[];
  onDownload: (resume: Resume) => void;
}

export function ResumeList({ resumes, onDownload }: ResumeListProps) {
  if (resumes.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow-sm">
        <FileText className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No resumes yet</h3>
        <p className="mt-1 text-sm text-gray-500">
          Get started by uploading your first resume
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {resumes.map((resume) => (
        <div key={resume.id} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-gray-400" />
                <h3 className="font-medium text-gray-900">{resume.original_filename}</h3>
              </div>
              <div className="space-y-1">
                <p className="text-sm">
                  <span className="text-gray-500">Status: </span>
                  <span className={`font-medium ${
                    resume.status === 'completed' ? 'text-green-600' :
                    resume.status === 'processing' ? 'text-blue-600' :
                    resume.status === 'error' ? 'text-red-600' :
                    'text-gray-600'
                  }`}>
                    {resume.status.charAt(0).toUpperCase() + resume.status.slice(1)}
                  </span>
                </p>
                {resume.error_message && (
                  <p className="text-sm text-red-600">
                    Error: {resume.error_message}
                  </p>
                )}
                <p className="text-xs text-gray-400">
                  Created: {new Date(resume.created_at).toLocaleDateString()} at{' '}
                  {new Date(resume.created_at).toLocaleTimeString()}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {resume.job_url && (
                <a
                  href={resume.job_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-gray-500 hover:text-gray-700"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span className="text-sm">View Job</span>
                </a>
              )}
              {resume.status === 'completed' && resume.optimized_file_url && (
                <button
                  onClick={() => onDownload(resume)}
                  className="flex items-center space-x-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Download className="h-4 w-4" />
                  <span>Download</span>
                </button>
              )}
              {resume.status === 'processing' && (
                <div className="flex items-center space-x-2 text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                  <span className="text-sm">Processing</span>
                </div>
              )}
              {resume.status === 'error' && (
                <div className="flex items-center space-x-2 text-red-600">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm">Failed</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}